// file: src/components/icons/FileStatusIcon.jsx
import React, { useEffect, useState, useRef } from 'react';
import styles from './FileStatusIcon.module.css';
import ReparseConfirmModal from '../modals/ReparseConfirmModal';
import { CircleCheckBig, Clock } from 'lucide-react';

function FileStatusIcon({ run, progress = 0, onRetry, onReparse, progress_msg, docName, type }) {
  const progressValue = typeof progress === 'number' ? progress : parseFloat(progress || 0);
  const [smoothProgress, setSmoothProgress] = useState(progressValue);
  const lastProgressRef = useRef(progressValue);
  const animationRef = useRef();
  
  const extractPhase = (msg) => {
    if (!msg || typeof msg !== 'string') return 'Processing';
    const lowerMsg = msg.toLowerCase();
    if (lowerMsg.includes('finalizing') || lowerMsg.includes('done') || lowerMsg.includes('complete')) {
      return 'Finalizing';
    } else if (lowerMsg.includes('index')) {
      return 'Indexing';
    } else if (lowerMsg.includes('embedding') || lowerMsg.includes('vector')) {
      return 'Embedding';
    } else if (lowerMsg.includes('chunk')) {
      return 'Chunking';
    } else if (lowerMsg.includes('ocr')) {
      return 'OCR';
    } else if (lowerMsg.includes('layout')) {
      return 'Layout Analysis';
    } else if (lowerMsg.includes('table')) {
      return 'Table Analysis';
    } else if (lowerMsg.includes('text process')) {
      return 'Text Processing';  
    } else if (lowerMsg.includes('pars')) {
      return 'Parsing';
    } else if (lowerMsg.includes('community')) {
      return 'Community Detection';
    } else if (lowerMsg.includes('raptor')) {
      return 'RAPTOR Processing';
    } else if (lowerMsg.includes('graph')) {
      return 'Graph Analysis';
    }
    return 'Processing';
  };
  
  let processingPhase = extractPhase(progress_msg);

  useEffect(() => {
    if (progressValue === smoothProgress) return;
    let raf;
    const animate = () => {
      setSmoothProgress(prev => {
        const target = progressValue;
        const current = parseFloat(prev);
        if (Math.abs(target - current) < 0.002) return target;
        return current + (target - current) * 0.15;
      });
      raf = requestAnimationFrame(animate);
      animationRef.current = raf;
    };
    animate();
    return () => raf && cancelAnimationFrame(raf);
  }, [progressValue]);

  const isRunning = (run === '1' || run === 1);
  const isDone = (run === '3' || run === 3 || (progressValue >= 0.99 && progressValue <= 1.01));
  const isFailedOrCanceled = (run === '2' || run === 2 || run === '4' || run === 4 || progressValue < 0);

  const handleReparse = (e) => {
    e.stopPropagation();
    if (onReparse) onReparse();
  };

  // --- JUPYTERLAB SPECIAL CASE: Show green CircleCheckBig for all done states ---
  if (type === 'jupyterlab' && !isRunning && !isFailedOrCanceled && isDone) {
    return (
      <CircleCheckBig
        size={22}
        strokeWidth={2.2}
        color="#22c55e"
        fill="none"
        style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}
        title="JupyterLab server linked"
      />
    );
  }
  // Also show for jupyterlab files that are not running/failed and not yet parsed (default to done icon)
  if (type === 'jupyterlab' && !isRunning && !isFailedOrCanceled && !isDone) {
    return (
      <CircleCheckBig
        size={22}
        strokeWidth={2.2}
        color="#22c55e"
        fill="none"
        style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}
        title="JupyterLab server linked"
      />
    );
  }

  if (type === 'blender' || type === 'freecad' || type === 'kicad' && !isRunning && !isFailedOrCanceled && isDone) {
    return (
      <CircleCheckBig
        size={22}
        strokeWidth={2.2}
        color="#22c55e"
        fill="none"
        style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}
        title={type === 'blender' ? 'Blender server linked' : type === 'freecad' ? 'FreeCAD server linked' : 'KiCad server linked'}
      />
    );
  }
  // Also show for kicad files that are not running/failed and not yet parsed (default to done icon)
  if (type === 'blender' || type === 'freecad' || type === 'kicad' && !isRunning && !isFailedOrCanceled && !isDone) {
    return (
      <CircleCheckBig
        size={22}
        strokeWidth={2.2}
        color="#22c55e"
        fill="none"
        style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}
        title={type === 'blender' ? 'Blender server linked' : type === 'freecad' ? 'FreeCAD server linked' : 'KiCad server linked'}
      />
    );
  }

  // --- DEFAULT LOGIC FOR OTHER FILE TYPES ---
  if (isRunning) {
    const clamped = Math.max(0, Math.min(smoothProgress, 1));
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const dash = clamped * circumference;
    let displayPercentage;
    if (clamped === 0) {
      displayPercentage = 0;
    } else if (clamped < 0.01) {
      displayPercentage = Math.max(0.1, Math.round(clamped * 1000) / 10);
    } else {
      displayPercentage = Math.round(clamped * 100);
    }
    const getPhaseTitle = () => {
      return `${processingPhase} - ${displayPercentage}%`;
    };
    return (
      <div className={styles.progressRingWrapper} title={getPhaseTitle()}>
        <svg className={styles.progressRing} viewBox="0 0 100 100">
          <circle
            className={styles.progressRingBg}
            cx="50"
            cy="50"
            r={radius}
            fill="transparent"
          />
          <circle
            className={styles.progressRingCircle}
            cx="50"
            cy="50"
            r={radius}
            fill="transparent"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={circumference - dash}
          />
        </svg>
        <span className={styles.progressLabel}>
          {displayPercentage}%
        </span>
      </div>
    );
  } else if (isDone) {
    return (
      <CircleCheckBig
        size={22}
        strokeWidth={2.2}
        color="#22c55e"
        fill="none"
        style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}
        title="Processing complete. Click to re-parse."
        onClick={handleReparse}
      />
    );
  } else if (isFailedOrCanceled) {
    return (
      <div
        className={styles.failedWrapper}
        title="Parsing failed or canceled. Click to retry."
        onClick={(e) => {
          e.stopPropagation();
          onRetry?.();
        }}
      >
        <svg
          className={styles.failIcon}
          viewBox="0 0 24 24"
          width="20"
          height="20"
        >
          <path d="M0 0h24v24H0V0z" fill="none"/>
          <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
        </svg>
      </div>
    );
  } else {
    // Not parsed yet - show pending icon
    return (
      <Clock
        size={20}
        strokeWidth={2}
        color="#94a3b8"
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
        title="Pending - not parsed yet"
      />
    );
  }
}

export default FileStatusIcon;






